/* The settings-bar field a reader finds a component with.
 *
 * In the bar rather than in the groups-and-components list, which is where the keyboard path
 * already lives: that panel is hidden until it takes focus, so a filter inside it would only ever
 * be found by someone who already tabbed into it. The question this answers is one a reader has
 * while looking at the picture with a mouse in their hand.
 */

import { connect, dispatchAction } from '@ardoq/rxbeach';
import { combineLatest, map } from 'rxjs';
import { SearchInput } from '@ardoq/forms';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { t, type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { network3dSearch$, network3DSearchChanged } from './network3dSearch$';

export type Network3DSearchFieldProps = {
  query: string;
  matchCount: number;
  i18nLocale: I18nLocale;
};

const toSummary = (matchCount: number, i18nLocale: I18nLocale): string =>
  matchCount === 0
    ? t(i18nLocale, 'views:network3d.search.noMatches', 'No matches')
    : t(
        i18nLocale,
        'views:network3d.search.matchCount',
        { count: matchCount },
        '{{count}} found'
      );

const Network3DSearchField = ({
  query,
  matchCount,
  i18nLocale,
}: Network3DSearchFieldProps) => {
  const isSearching = query.trim() !== '';
  const summary = isSearching ? toSummary(matchCount, i18nLocale) : undefined;
  const placeholder = t(
    i18nLocale,
    'views:network3d.search.placeholder',
    'Find a component'
  );
  return (
    <>
      <SearchInput
        value={query}
        onValueChange={value => dispatchAction(network3DSearchChanged(value))}
        placeholder={placeholder}
        /* The same words as the placeholder. Without it `SearchInput` names the field "Search",
           which tells a screen-reader or voice-control reader what the control is and not what it
           searches - and the row beside it is already called Search. */
        aria-label={placeholder}
        /* Under the field rather than beside it. The settings panel gives a row's control about
           220px, so a count set alongside a field wide enough to hold a component name is pushed
           off the edge - which is what it did. */
        helperText={summary}
        i18nLocale={i18nLocale}
        dataClickId="network-3d-search"
      />
      {/* `helperText` is a description rather than an announcement: a screen reader reads it when
          the field takes focus, not when the number under it changes. Present from the first
          render, because a live region that arrives with its first message announces nothing. */}
      <VisuallyHidden elementType="span">
        <span role="status" aria-live="polite">
          {summary ?? ''}
        </span>
      </VisuallyHidden>
    </>
  );
};

const viewModel$ = combineLatest([network3dSearch$, i18nLocale$]).pipe(
  map(([{ query, matchCount }, i18nLocale]) => ({
    query,
    matchCount,
    i18nLocale,
  }))
);

export const ConnectedNetwork3DSearchField = connect(
  Network3DSearchField,
  viewModel$
);
